import { useMemo } from "react";
import { Chip } from "@mui/material";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { StatusGeradaChip } from "@/components/escala/StatusGeradaChip";
import { useRelatorio } from "@/components/escala/useRelatorio";
import { formatPercentual, plural } from "@/components/escala/relatorio";
import { useAutoresDaEscala } from "@/hooks/useAutoresDaEscala";
import {
  EscalaCaracteristicaLabels,
  EscalaTipoLabels,
  STATUS_GERADA_LABEL,
  formatDataBR,
  formatDataHoraBR,
  relAcompanhamento,
  rotuloLoja,
  type EscalaCaracteristica,
  type EscalaTipo,
  type Gerada,
  type LinhaAcompanhamento,
} from "@/api/escala.service";


const AUTOR_DESCONHECIDO = "Usuário não identificado";

const atrasadasPrimeiro = (a: LinhaAcompanhamento, b: LinhaAcompanhamento) =>
  Number(b.atrasada) - Number(a.atrasada) || a.escala_nome.localeCompare(b.escala_nome);

const statusLabel = (l: LinhaAcompanhamento) => (l.status ? STATUS_GERADA_LABEL[l.status as Gerada["status"]] : "Nunca gerada");

function indicadoresAcompanhamento(linhas: LinhaAcompanhamento[]): [Indicador, Indicador, Indicador, Indicador] {
  const contar = (status?: LinhaAcompanhamento["status"]) => linhas.filter((l) => (l.status || undefined) === status).length;
  const aprovadas = contar("APROVADA");
  const rascunho = contar("RASCUNHO");
  const validadas = contar("VALIDADA");
  const nunca = contar(undefined);
  const atrasadas = linhas.filter((l) => l.atrasada).length;
  const lojasPendentes = new Set(linhas.filter((l) => l.status !== "APROVADA").map((l) => l.loja_id)).size;
  return [
    {
      titulo: "Aprovadas",
      valor: `${aprovadas} de ${linhas.length}`,
      nota: !linhas.length
        ? "nenhuma escala ativa"
        : lojasPendentes
          ? `${formatPercentual(aprovadas / linhas.length)} · ${plural(lojasPendentes, "loja")} com pendência`
          : "todas as lojas em dia",
      tom: linhas.length && aprovadas === linhas.length ? "bom" : "neutro",
    },
    { titulo: "Em andamento", valor: rascunho + validadas, nota: `${rascunho} em rascunho · ${validadas} aguardando aprovação` },
    { titulo: "Nunca geradas", valor: nunca, nota: "sem nenhuma versão no mês", tom: nunca ? "atencao" : "neutro" },
    { titulo: "Atrasadas", valor: atrasadas, nota: "fora do prazo de aprovação", tom: atrasadas ? "critico" : "bom" },
  ];
}

// Status da competência por escala ativa da loja, cruzado com o prazo do client.
export function EscalaRelAcompanhamentoTab() {
  const r = useRelatorio({ buscar: relAcompanhamento, ordenar: atrasadasPrimeiro, mensagemErro: "Erro ao carregar o acompanhamento das escalas" });
  const autores = useAutoresDaEscala(r.clientId);
  const indicadores = useMemo(() => indicadoresAcompanhamento(r.linhas), [r.linhas]);

  const caracteristicaLabel = (l: LinhaAcompanhamento) => EscalaCaracteristicaLabels[l.caracteristica as EscalaCaracteristica];

  const tipoLabel = (l: LinhaAcompanhamento) => EscalaTipoLabels[l.tipo as EscalaTipo] ?? l.tipo;
  const autorLabel = (id?: string) => (id ? autores.get(id) ?? AUTOR_DESCONHECIDO : "");

  const columns: Column<LinhaAcompanhamento>[] = [
    { key: "escala_nome", label: "Escala" },
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(r.lojas, l.loja_id), exportValue: (l) => rotuloLoja(r.lojas, l.loja_id) },
    { key: "tipo", label: "Tipo", render: tipoLabel, exportValue: tipoLabel },
    { key: "caracteristica", label: "Característica", render: caracteristicaLabel, exportValue: caracteristicaLabel },
    { key: "versao", label: "Versão", render: (l) => l.versao ?? "", exportValue: (l) => l.versao ?? "" },
    {
      key: "status",
      label: "Status",
      render: (l) => (l.status ? <StatusGeradaChip status={l.status as Gerada["status"]} /> : <Chip size="small" label="Nunca gerada" />),
      exportValue: statusLabel,
    },
    {
      key: "validada_por",
      label: "Validada por",
      render: (l) => autorLabel(l.validada_por),
      exportValue: (l) => autorLabel(l.validada_por),
    },
    { key: "validada_em", label: "Validada em", render: (l) => formatDataHoraBR(l.validada_em), exportValue: (l) => formatDataHoraBR(l.validada_em) },
    {
      key: "aprovada_por",
      label: "Aprovada por",
      render: (l) => autorLabel(l.aprovada_por),
      exportValue: (l) => autorLabel(l.aprovada_por),
    },
    { key: "aprovada_em", label: "Aprovada em", render: (l) => formatDataHoraBR(l.aprovada_em), exportValue: (l) => formatDataHoraBR(l.aprovada_em) },
    { key: "prazo_fim_janela", label: "Prazo", render: (l) => formatDataBR(l.prazo_fim_janela), exportValue: (l) => formatDataBR(l.prazo_fim_janela) },
    {
      key: "atrasada",
      label: "Atrasada",
      render: (l) => (l.atrasada ? <Chip size="small" label="Atrasada" color="error" /> : ""),
      exportValue: (l) => (l.atrasada ? "Sim" : "Não"),
    },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={<FiltroRelatorio estado={r} comCargo={false} />}
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "acompanhamento", aba: "Acompanhamento" }}
      competencia={r.filtros}
    >
      <DataTable columns={columns} {...r.tabela} />
    </RelatorioLayout>
  );
}
